import { Assessment } from "@mui/icons-material"
import AddAlertOutlinedIcon from "@mui/icons-material/AddAlertOutlined"
import ModeEditIcon from "@mui/icons-material/ModeEdit"
import PowerIcon from "@mui/icons-material/Power"

import { useGetLctTtrRbac } from "./useGetLctTtrRbac"
import { useGetUserData } from "./useGetUserData"
import { Datasets } from "../constants/enums"
import {
    COMPLAINT_CONFORMATION,
    COMPLIANT,
    DRAFT_AMENDMENT,
    DRAFT_DEVICE,
} from "../constants/generalConstants"
import { TT_LCT_TTR_DRAFT_CONTINUE } from "../constants/tooltips"
import { ISingleDevice } from "../services/middleware/mappers/singleDeviceMapper"
import { HeaderAction } from "../webpages/lowCarbonTechDatabases/headerBanner"

type PageActions = {
    onClickRaiseAlert: () => void
    onClickAssessDevice: () => void
    onClickAmendDevice: () => void
    onClickNewVersion: () => void
    onClickContinueDraftAmendment: () => void
    onClickContinueDraftversion: () => void
    onClickViewDraftAmendment: () => void
    onClickViewDraftVersion: () => void
    onClickDeleteDevice: () => void
}

const useGetHeaderActions = (
    data: ISingleDevice,
    dataset: Datasets,
    pageActions: PageActions
): HeaderAction[] => {
    const { alert, status, draftAmendmentRef, draftVersionRef } = data.header

    const {
        onClickRaiseAlert,
        onClickAssessDevice,
        onClickAmendDevice,
        onClickContinueDraftAmendment,
        onClickContinueDraftversion,
        onClickNewVersion,
        onClickViewDraftAmendment,
        onClickViewDraftVersion,
        onClickDeleteDevice: onClickDelete,
    } = pageActions

    const isV2gDevice = !!data.header.linkedInverterRef

    const { canRaiseAlerts, canPublishDevice } = useGetLctTtrRbac(
        data.header.deviceRef,
        dataset,
        isV2gDevice
    )

    const {
        isEnaSysAdmin,
        isGenAssessor,
        isDemandAssessor,
        isV2gAssessor,
        isManuApproved,
    } = useGetUserData()

    // Device Info

    const isDraftDevice = status === DRAFT_DEVICE
    const isDraftAmendment = status === DRAFT_AMENDMENT
    const isNotADraft = !isDraftAmendment && !isDraftDevice

    const isTTRDataset =
        dataset === Datasets.TTR || dataset === Datasets.TTR_DRAFT

    const canDeviceBeAmended = status !== COMPLIANT

    const hasDraftVersion = !!draftVersionRef
    const hasDraftAmendment = !!draftAmendmentRef

    // User Permissions

    const isAssessor =
        (isTTRDataset && isGenAssessor) ||
        (!isTTRDataset && isDemandAssessor) ||
        (!isTTRDataset && isV2gAssessor && isV2gDevice)

    const canAssessDevice = isAssessor && isNotADraft
    const canAmendDevice = isEnaSysAdmin || isManuApproved

    // Header actions

    const headerActions: HeaderAction[] = []

    if (isNotADraft && canRaiseAlerts && !alert) {
        const alertAction: HeaderAction = {
            text: "Raise Alert",
            color: "primary",
            endIcon: <AddAlertOutlinedIcon />,
            onClick: onClickRaiseAlert,
        }
        headerActions.push(alertAction)
    }
    if (canAssessDevice) {
        const assessAction: HeaderAction = {
            text: "Assess Device",
            color: "primary",
            endIcon: <Assessment />,
            onClick: onClickAssessDevice,
        }
        headerActions.push(assessAction)
    }

    if (
        canDeviceBeAmended &&
        canAmendDevice &&
        !hasDraftAmendment &&
        isNotADraft
    ) {
        const amendAction: HeaderAction = {
            text: "Amend Device",
            color: "primary",
            endIcon: <PowerIcon />,
            onClick: onClickAmendDevice,
            disabled: [COMPLAINT_CONFORMATION].includes(data?.header?.status),
            tooltip:
                "An amendment should only be submitted if there is an error or omission in the original registration. If the device has been modified or updated (hardware, firmware or software), please issue a version instead",
        }
        headerActions.push(amendAction)
    }

    if (canAmendDevice && hasDraftAmendment) {
        const draftAmendAction: HeaderAction = {
            text: "View Draft Amendment",
            color: "primary",
            endIcon: <ModeEditIcon />,
            onClick: onClickViewDraftAmendment,
            tooltip:
                "A draft amendment of this device is in progress. Click here to view it!",
        }
        headerActions.push(draftAmendAction)
    }

    if (canPublishDevice && !hasDraftVersion && isNotADraft) {
        const newVersionAction: HeaderAction = {
            text: "New Version",
            color: "primary",
            endIcon: <PowerIcon />,
            onClick: onClickNewVersion,
            disabled: data?.header?.status !== COMPLIANT,
            tooltip:
                "A new version of a registration should only be submitted if the device hardware, firmware or software has been modified or updated. If there is an error or omission in the original registration, please issue an amendment. A new device version can only be created from compliant devices",
        }
        headerActions.push(newVersionAction)
    }

    if (canPublishDevice && hasDraftVersion) {
        const draftNewVersionAction: HeaderAction = {
            text: "View Draft Version",
            color: "primary",
            endIcon: <ModeEditIcon />,
            onClick: onClickViewDraftVersion,
            tooltip:
                "A draft new version of this device is in progress. Click here to view it!",
        }
        headerActions.push(draftNewVersionAction)
    }

    if (canAmendDevice && isDraftAmendment) {
        const continueAmendmentAction: HeaderAction = {
            text: "Continue Draft Amendment",
            color: "primary",
            endIcon: <ModeEditIcon />,
            onClick: onClickContinueDraftAmendment,
            tooltip: TT_LCT_TTR_DRAFT_CONTINUE,
        }
        headerActions.push(continueAmendmentAction)
    }

    if (canPublishDevice && isDraftDevice) {
        const continueVersionAction: HeaderAction = {
            text: "Continue Draft Version",
            color: "primary",
            endIcon: <ModeEditIcon />,
            onClick: onClickContinueDraftversion,
            tooltip: TT_LCT_TTR_DRAFT_CONTINUE,
        }
        headerActions.push(continueVersionAction)
    }

    if (canPublishDevice && isNotADraft) {
        const deleteDeviceAction: HeaderAction = {
            text: "Delete Device",
            color: "error",
            endIcon: <PowerIcon />,
            onClick: onClickDelete,
        }
        headerActions.push(deleteDeviceAction)
    }

    return headerActions
}

export default useGetHeaderActions
